// Hub canton/région — ex. Chauffeur privé dans le canton de Vaud
// Niveau intermédiaire du maillage : Suisse → canton → ville.
import { Link } from "react-router-dom";
import { ArrowRight, Building2, Check, MapPin, Plane } from "lucide-react";
import { JsonLd, RouteLinks, SeoPage, TrustChips, useHreflang, useSeo } from "@/components/seo/Seo";
import {
  AIRPORTS, BASE_URL, BOOKING_PATH, CANTONS, LANGS, S, T, citiesInCanton, p, routesFrom,
} from "@/lib/seoContent";

export default function CantonHub({ lang, cantonId }) {
  const canton = CANTONS[cantonId];
  const t = T[lang];
  const airport = canton ? AIRPORTS[canton.airport] : null;
  const path = canton ? p(lang, S.chauffeur[lang], canton.slug[lang]) : `/${lang}`;
  const alternates = canton ? Object.fromEntries(LANGS.map((l) => [l, p(l, S.chauffeur[l], canton.slug[l])])) : null;

  useSeo({ lang, title: canton ? canton.title[lang] : "MILES", description: canton ? canton.meta[lang] : "", path, type: "canton", id: cantonId, noindex: canton?.indexable === false });
  useHreflang(alternates);

  if (!canton) return null;

  const cities = citiesInCanton(cantonId);
  const routes = [...new Set([...routesFrom(canton.airport), ...cities.flatMap((c) => routesFrom(c.id))])].slice(0, 6);

  const crumbs = [
    { label: t.home, to: p(lang) },
    { label: { fr: "Chauffeur privé", en: "Chauffeur service", de: "Chauffeurservice" }[lang], to: p(lang, S.chauffeur[lang]) },
    { label: canton.name[lang] },
  ];

  const jsonLd = [
    { "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: crumbs.map((c, i) => ({ "@type": "ListItem", position: i + 1, name: c.label, ...(c.to ? { item: `${BASE_URL}${c.to}` } : {}) })) },
    { "@context": "https://schema.org", "@type": "Service", serviceType: "Chauffeur service",
      provider: { "@type": "Organization", name: "MILES", url: BASE_URL },
      areaServed: { "@type": "AdministrativeArea", name: canton.name.en }, name: `MILES — ${canton.name.en}` },
  ];

  return (
    <SeoPage lang={lang} breadcrumbs={crumbs} testId={`seo-canton-${cantonId}`}>
      {jsonLd.map((j, i) => <JsonLd key={i} id={`canton-${i}`} data={j} />)}

      <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-miles">{canton.name[lang]}</p>
      <h1 className="mt-3 max-w-3xl text-3xl font-bold leading-tight text-ink sm:text-5xl" data-testid="canton-h1">{canton.h1[lang]}</h1>
      <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate">{canton.intro[lang]}</p>
      <div className="mt-7">
        <Link to={BOOKING_PATH} data-testid="canton-book-cta"
          className="inline-flex h-12 items-center gap-2 rounded-xl bg-miles px-6 text-sm font-semibold text-white transition-opacity hover:opacity-90">
          {t.book_cta} <ArrowRight className="h-4 w-4" strokeWidth={2} />
        </Link>
      </div>

      <ul className="mt-10 space-y-2.5">
        {canton.facts[lang].map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm text-slate">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" strokeWidth={2} /> {f}
          </li>
        ))}
      </ul>

      <div className="mt-8"><TrustChips lang={lang} ski={cantonId !== "vaud"} /></div>

      {/* Villes desservies dans le canton */}
      <section className="mt-14" data-testid="canton-cities">
        <h2 className="text-xl font-bold text-ink">{t.cities_served}</h2>
        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {cities.map((city) => (
            <Link key={city.id} to={p(lang, S.chauffeur[lang], city.slug[lang])} data-testid={`canton-city-${city.id}`}
              className="group rounded-2xl border border-ink/10 bg-white p-6 transition-colors hover:border-miles/40">
              <MapPin className="h-5 w-5 text-miles" strokeWidth={1.5} />
              <p className="mt-3 text-base font-bold text-ink">{t.chauffeur_in} {city.name[lang]}</p>
              <p className="mt-1.5 line-clamp-2 text-sm text-slate">{city.intro[lang]}</p>
              <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-miles">
                {t.view_city} <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Aéroport pertinent */}
      {airport && (
        <section className="mt-14" data-testid="canton-airport">
          <Link to={p(lang, S.airport[lang], airport.slug[lang])} data-testid="canton-airport-link"
            className="group flex items-center justify-between rounded-2xl border border-ink/10 bg-white px-6 py-5 transition-colors hover:border-miles/40">
            <span className="flex items-center gap-4">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-miles-deep font-mono text-xs font-bold text-white">{airport.iata}</span>
              <span>
                <span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-slate">{t.airport_of}</span>
                <span className="mt-1 flex items-center gap-2 text-base font-bold text-ink">
                  <Plane className="h-4 w-4 text-miles" strokeWidth={1.5} /> {airport.name[lang]}
                </span>
              </span>
            </span>
            <ArrowRight className="h-5 w-5 text-slate transition-transform group-hover:translate-x-1 group-hover:text-miles" strokeWidth={1.5} />
          </Link>
        </section>
      )}

      <div className="mt-14 space-y-14">
        {routes.length > 0 && (
          <RouteLinks title={`${t.popular_from} ${canton.name[lang]}`} routes={routes} lang={lang} testId="canton-routes" />
        )}

        {/* MILES Business */}
        <section className="rounded-2xl border border-ink/10 bg-white p-6 sm:p-8" data-testid="canton-business">
          <h2 className="flex items-center gap-2 text-xl font-bold text-ink">
            <Building2 className="h-5 w-5 text-miles" strokeWidth={1.5} /> {t.business_city_title.replace("{city}", canton.name[lang])}
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate">{t.business_city_sub}</p>
          <Link to={p(lang, S.business)} data-testid="canton-business-link"
            className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-miles">
            {t.business_title} <ArrowRight className="h-4 w-4" strokeWidth={2} />
          </Link>
        </section>
      </div>

      {/* CTA final */}
      <section className="mt-14 rounded-3xl bg-miles-deep px-7 py-10 text-center sm:px-10" data-testid="canton-final-cta">
        <h2 className="text-2xl font-bold text-white">{t.final_cta_title}</h2>
        <p className="mx-auto mt-2 max-w-md text-sm text-white/70">{t.final_cta_sub}</p>
        <div className="mt-6">
          <Link to={BOOKING_PATH} data-testid="canton-final-book-cta"
            className="inline-flex h-12 items-center gap-2 rounded-xl bg-miles px-8 text-sm font-semibold text-white transition-opacity hover:opacity-90">
            {t.book_cta} <ArrowRight className="h-4 w-4" strokeWidth={2} />
          </Link>
        </div>
      </section>
    </SeoPage>
  );
}
